import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CreateGroupPayload } from '../../service/api';
import useGroupStore from '../../store/group';

export default function CreateGroupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { createGroup } = useGroupStore();

  const isFirstTimeManager =
    location.state?.from &&
    location.state.from.pathname.startsWith('/manager/');

  const [formData, setFormData] = useState<CreateGroupPayload>({
    title: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await createGroup(formData);
      navigate('/manager/group');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light">
      <Container className="py-4">
        <Row className="mb-4 align-items-center">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate('/')}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="mb-0">{t('groups.createModal.title')}</h2>
          </Col>
        </Row>

        {isFirstTimeManager && (
          <Row className="mb-4">
            <Col md={6} className="mx-auto">
              <Alert variant="info">
                <h5>{t('groups.welcomeManager')}</h5>
                <p className="mb-0">{t('groups.createFirstGroup')}</p>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('groups.createModal.groupName')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={e =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder={t('groups.createModal.namePlaceholder')}
                      required
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !formData.title}
                      className="flex-grow-1"
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t('groups.createModal.creating')}
                        </>
                      ) : (
                        t('groups.createModal.create')
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
